import { useGetApiAdminIntacctCompanyInfo } from '@sit/client-shared';
import { skipToken } from '@tanstack/react-query';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/__next__';
import Card, {
  CardColumn,
  CardFooter,
  CardRow,
} from 'carbon-react/lib/components/card';
import Divider from 'carbon-react/lib/components/divider';
import Form from 'carbon-react/lib/components/form';
import Loader from 'carbon-react/lib/components/loader/__next__';
import Message from 'carbon-react/lib/components/message';
import Password from 'carbon-react/lib/components/password';
import Textbox from 'carbon-react/lib/components/textbox';
import Typography from 'carbon-react/lib/components/typography';
import { type FormEventHandler, useState } from 'react';
import GoBackButton from '../../components/GoBackButton';
import { useProvisionTestJob } from '../../helpers/use-provision-test-job';
import MissingPermissions from '../MissingPermissions/MissingPermissions';

const ProvisionTest = () => {
  const [companyId, setCompanyId] = useState('');
  const [password, setPassword] = useState('');
  const [userId, setUserId] = useState('');
  const [customIntacctEndpoint, setCustomIntacctEndpoint] = useState('');
  const [inputErrors, setInputErrors] = useState<Record<string, boolean>>({});
  const {
    run: checkPermissions,
    isPending,
    result: data,
    error,
  } = useProvisionTestJob();
  const { data: intacctCompanyInfo } = useGetApiAdminIntacctCompanyInfo(
    companyId
      ? {
          pathParams: {
            intacctCompanyId: companyId,
          },
        }
      : skipToken,
  );

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    if (isPending || !companyId) return;
    checkPermissions({
      companyId,
      password,
      userId,
      customIntacctEndpoint: customIntacctEndpoint || undefined,
    });
  };

  const validateInput = (key: string, value: string) => {
    const _errs = { ...inputErrors };
    if (!value) {
      _errs[key] = true;
    } else {
      delete _errs[key];
    }
    setInputErrors(_errs);
  };

  return (
    <div className="provision-container">
      <GoBackButton />
      <div className="provision-content">
        <div className="provision">
          <Card
            footer={
              <CardFooter>
                {error ? (
                  <Box p={2} backgroundColor="white">
                    <Message variant="error">{error.message}</Message>
                  </Box>
                ) : null}
                {!!data?.missingPermissions && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    flexDirection="column"
                    p={2}
                    backgroundColor="white"
                  >
                    {data.missingPermissions.length === 0 ? (
                      <div>The WSU has all required permissions</div>
                    ) : (
                      <MissingPermissions
                        missing={data.missingPermissions}
                        companyTreeSettingsId={
                          intacctCompanyInfo?.companyTreeSettingsId
                        }
                      />
                    )}
                  </Box>
                )}
              </CardFooter>
            }
          >
            <Form
              errorCount={Object.keys(inputErrors).length}
              paddingBottom={2}
              saveButton={
                <Button
                  disabled={Object.keys(inputErrors).length > 0}
                  key="verify"
                  type="submit"
                >
                  {isPending ? (
                    <Loader
                      loaderType="ring"
                      variant="inline"
                      size="small"
                      loaderLabel="Verifying..."
                    />
                  ) : (
                    'Verify'
                  )}
                </Button>
              }
              onSubmit={handleSubmit}
            >
              <CardRow pb={0}>
                <CardColumn align="left">
                  <Typography variant="h1" mt={2}>
                    Enter Web Services User credentials
                  </Typography>
                </CardColumn>
              </CardRow>
              <Divider type="horizontal" mt={2} mb={2} />
              <Textbox
                required
                error={
                  inputErrors.companyId ? 'This field is required' : undefined
                }
                label="Company ID"
                labelInline={false}
                name="companyId"
                onBlur={() => validateInput('companyId', companyId)}
                onChange={(e) => setCompanyId(e.target.value)}
                // @ts-expect-error -- carbon-react doesn't use type but it exists
                type="text"
                value={companyId}
              />
              <Textbox
                required
                error={
                  inputErrors.userId ? 'This field is required' : undefined
                }
                label="Web Services User ID"
                labelInline={false}
                name="userId"
                onBlur={() => validateInput('userId', userId)}
                onChange={(e) => setUserId(e.target.value)}
                // @ts-expect-error -- carbon-react doesn't use type but it exists
                type="text"
                value={userId}
              />
              <Password
                required
                error={
                  inputErrors.password ? 'This field is required' : undefined
                }
                label="Web Services User password"
                labelInline={false}
                name="password"
                onBlur={() => validateInput('password', password)}
                onChange={(e) => setPassword(e.target.value)}
                value={password}
              />
              <Textbox
                label="Custom Intacct endpoint"
                labelInline={false}
                name="customIntacctEndpoint"
                placeholder="https://api.intacct.com/ia/xml/xmlgw.phtml"
                onChange={(e) => setCustomIntacctEndpoint(e.target.value)}
                // @ts-expect-error typing broken for carbon-react, remove this once they fix it
                type="text"
                value={customIntacctEndpoint}
              />
            </Form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProvisionTest;
